/**
 * GTM Helper Functions
 *
 * Client utilities for page_type, menu_category, and market_ecom, plus
 * server-side GA4 ecommerce payload builders for the multibrand BFF.
 */

import { ACCOUNT_METADATA, getMetaValue } from '@/lib/auth/metadata';
import { convertLocaleToApiParams } from '@/lib/configuration/api-params';
import { getCurrencyFromChannel } from '@/lib/configuration/channels';
import type { EcommerceReadySource } from '@/lib/configuration/ecommerce';
import { BRAND_NAME, getCountryCodeFromChannel } from '@/lib/const';
import { isNoTaxCountry, isTaxIncluded } from '@/lib/tax';
import {
  fetchProductsForGtmBySkus,
  type GtmProductVariant,
} from '@/lib/gtm/fetchProductsForGtmBySkus';
import {
  marketEcomCountries,
  menuCategoryMapping,
  pageTypeMapping,
  PAGE_TYPE,
  regExpMapping,
} from './const';
import type { GtmLineInput } from './ecommerceSchemas';
import type { EcommercePayload, Item } from './types';

/**
 * Strip the locale prefix from a pathname.
 * Locale format: /{country}-{language}/... (e.g., /us-en/watches/all/, /hk-zht/watches/all/)
 */
export function stripLocalePrefix(pathname: string): string {
  return pathname.replace(/^\/[a-z]{2}-[a-z]{2,3}/, '') || '/';
}

/**
 * Derive the GTM page_type from a pathname (locale already stripped).
 */
export function getPageType(pathname: string): string {
  const normalized = stripLocalePrefix(pathname);
  const withTrailingSlash = normalized.endsWith('/') ? normalized : `${normalized}/`;

  const staticMatch = pageTypeMapping[withTrailingSlash];
  if (staticMatch) return staticMatch;

  for (const { pattern, pageType } of regExpMapping) {
    if (pattern.test(withTrailingSlash)) return pageType;
  }

  return 'content page';
}

/**
 * Derive the GTM menu_category from a pathname.
 */
export function getMenuCategory(pathname: string): string {
  const normalized = stripLocalePrefix(pathname);
  if (normalized === '/') return 'homepage';

  const withTrailingSlash = normalized.endsWith('/') ? normalized : `${normalized}/`;
  if (
    withTrailingSlash.startsWith('/store/checkout/') ||
    withTrailingSlash.startsWith('/store/order/')
  ) {
    return 'checkout your order';
  }

  if (
    withTrailingSlash.startsWith('/my-account/login/') ||
    withTrailingSlash.startsWith('/my-account/register/')
  ) {
    return PAGE_TYPE.LOGIN_OR_ACCOUNT_CREATION;
  }

  const firstSegment = normalized.split('/').find(Boolean) || '';
  return menuCategoryMapping[firstSegment] || 'other';
}

/**
 * Determine if the country has full ecommerce or is catalog-only.
 * Uses the static map; pair with `resolveMarketEcom` + readiness for catalog overrides.
 */
export function getMarketEcom(country: string): 'yes' | 'no' {
  return marketEcomCountries[country.toUpperCase()] ?? 'no';
}

/**
 * Resolve the GTM `market_ecom` value from the resolved e-commerce readiness.
 *
 * Legacy (gating off): catalog-only markets (`ready: false`) always report `no`;
 * otherwise use the country map. Gating on: derive directly from config readiness (AC6).
 */
export function resolveMarketEcom(
  source: EcommerceReadySource,
  ready: boolean,
  country: string
): 'yes' | 'no' {
  if (source === 'legacy') {
    return ready ? getMarketEcom(country) : 'no';
  }
  return ready ? 'yes' : 'no';
}

/**
 * Extract country code (uppercase) from locale slug.
 * Locale format: {country}-{language} (e.g., "us-en" → "US")
 */
export function getCountryFromLocale(locale: string): string {
  return (locale.split('-')[0] || '').toUpperCase();
}

/**
 * Extract language code (lowercase) from locale slug.
 * Locale format: {country}-{language} (e.g., "us-en" → "en")
 */
export function getLanguageFromLocale(locale: string): string {
  return (locale.split('-')[1] || locale).toLowerCase();
}

/**
 * Determine if debug mode is active via env var.
 */
export function isDebugMode(): boolean {
  return process.env.NEXT_PUBLIC_GA_DEBUG_MODE === 'true';
}

/**
 * Read the geo_country cookie set by the proxy (IP-based geolocation).
 * Falls back to empty string if cookie not available.
 */
export function getCountryIP(): string {
  if (globalThis.document === undefined) return '';
  const match = /(?:^|;\s*)geo_country=([^;]*)/.exec(globalThis.document.cookie);
  return match?.[1] || '';
}

/**
 * Read user data from the non-httpOnly `user` cookie.
 * Returns id, email, and Salesforce CID, or undefined values when not logged in.
 */
export function getUserFromCookie(): {
  id: string | undefined;
  email: string | undefined;
  salesForceId: string | undefined;
} {
  const emptyUser = { id: undefined, email: undefined, salesForceId: undefined };

  if (globalThis.document === undefined) return emptyUser;
  const match = /(?:^|;\s*)user=([^;]*)/.exec(globalThis.document.cookie);
  if (!match?.[1]) return emptyUser;

  try {
    const decoded = decodeURIComponent(match[1]);
    const user = JSON.parse(decoded);
    const metadata = Array.isArray(user.metadata) ? user.metadata : [];
    const salesForceId = getMetaValue(metadata, ACCOUNT_METADATA.salesForceId);

    return {
      id: user.id || undefined,
      email: user.email || undefined,
      salesForceId: salesForceId || undefined,
    };
  } catch {
    return emptyUser;
  }
}

/**
 * SHA-256 hash an email address for GTM user identification.
 * Returns undefined if email is not available or hashing fails.
 */
export async function hashEmail(email: string | undefined): Promise<string | undefined> {
  if (!email || globalThis.window === undefined) return undefined;

  try {
    const normalized = email.trim().toLowerCase();
    const encoder = new TextEncoder();
    const data = encoder.encode(normalized);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}

function normalizeItemId(sku: string): string {
  return sku.replaceAll('.', '_');
}

function getGender(values: string[] | undefined): string {
  if (!values?.length) return 'no_gender';
  if (values.includes('men')) {
    return values.includes('women') ? 'no_gender' : 'men';
  }
  if (values.includes('women')) return 'women';
  return 'no_gender';
}

function getCaseMaterialAttributeName(
  attribute:
    | {
        values?: Array<{
          name?: string | null;
          plainText?: string | null;
          translation?: { name?: string | null; plainText?: string | null } | null;
        }>;
      }
    | null
    | undefined
): string | undefined {
  const value = attribute?.values?.[0];
  const label = value?.plainText || value?.name;
  return label?.toUpperCase() ?? undefined;
}

/**
 * Strap colour for analytics — human label from plainText only.
 * For wbr-color, translation.name / name are PIM codes (V-STRAP-COLOR-*).
 */
function getStrapColorAttributeName(
  attribute:
    | {
        values?: Array<{
          plainText?: string | null;
          translation?: { name?: string | null; plainText?: string | null } | null;
        }>;
      }
    | null
    | undefined
): string | undefined {
  const value = attribute?.values?.[0];
  const label = value?.plainText || value?.translation?.plainText;
  return label?.toLowerCase() || undefined;
}

const GTM_APPOINTMENT_MOTIVE_BY_API_VALUE: Record<string, string> = {
  discover_and_try_gallet: 'discover_and_try',
  purchase_gallet: 'purchase',
  find_a_new_strap_gallet: 'find_a_new_strap',
};

export function mapAppointmentMotiveForGtm(motive: string): string {
  return GTM_APPOINTMENT_MOTIVE_BY_API_VALUE[motive] ?? motive;
}

function getAttributeSlug(
  attribute: { values?: Array<{ slug?: string | null }> } | null | undefined
): string | undefined {
  return attribute?.values?.[0]?.slug ?? undefined;
}

function getAttributeSlugs(
  attribute: { values?: Array<{ slug?: string | null }> } | null | undefined
): string[] {
  return (
    attribute?.values?.map((value) => value.slug).filter((slug): slug is string => !!slug) ?? []
  );
}

function resolveUnitPrice(
  gross: { amount: number; currency: string } | null | undefined,
  net: { amount: number; currency: string } | null | undefined,
  isVatIncluded: boolean
): { price: number; currency: string } {
  const selected = isVatIncluded ? gross : (net ?? gross);
  return {
    price: selected?.amount ?? 0,
    currency: selected?.currency ?? gross?.currency ?? net?.currency ?? '',
  };
}

function buildFallbackItem(
  sku: string,
  index: number,
  fallbackCurrency: string,
  itemListName?: string,
  coupon?: string
): Item {
  const normalized = normalizeItemId(sku);
  return {
    index,
    item_id: normalized,
    item_name: sku.toLowerCase(),
    item_brand: BRAND_NAME.toLowerCase(),
    item_category: 'product',
    item_sub_category: 'regular',
    item_list_name: itemListName,
    price: 0,
    quantity: 1,
    currency: fallbackCurrency,
    item_stock: 'no',
    item_wishlist_compared: 'no',
    coupon,
    discount: 0,
  };
}

export function buildItemFromGtmVariant(
  variant: GtmProductVariant,
  index: number,
  {
    isVatIncluded,
    itemListName,
    coupon,
  }: {
    isVatIncluded: boolean;
    itemListName?: string;
    coupon?: string;
  }
): Item {
  const product = variant.product;
  const sku = variant.sku ?? '';
  const gross = variant.pricing?.price?.gross;
  const net = variant.pricing?.price?.net;
  const { price, currency } = resolveUnitPrice(gross, net, isVatIncluded);

  return {
    index,
    item_id: normalizeItemId(sku),
    item_name: product.name.toLowerCase(),
    item_brand: BRAND_NAME.toLowerCase(),
    item_category: product.productType?.slug ?? 'product',
    item_sub_category: 'regular',
    item_collection: product.collections?.[0]?.slug,
    item_list_name: itemListName,
    item_case_material: getCaseMaterialAttributeName(product.caseMaterial),
    item_dial_color: getAttributeSlug(product.dialColor),
    item_strap_color: getStrapColorAttributeName(product.strapColor),
    item_strap_material: getAttributeSlug(product.strapMaterial),
    item_strap_size: getAttributeSlug(product.size),
    item_gender: getGender(getAttributeSlugs(product.comCategories)),
    item_stock: (variant.quantityAvailable ?? 0) > 0 ? 'yes' : 'no',
    item_wishlist_compared: 'no',
    price,
    quantity: 1,
    currency,
    coupon,
    discount: 0,
  };
}

function buildItemFromLine(
  line: GtmLineInput,
  index: number,
  isVatIncluded: boolean,
  fallbackCurrency: string,
  coupon?: string
): Item {
  const variant = line.variant;
  const product = variant?.product;
  const sku = line.sku || variant?.sku || '';
  const unitGross = line.unitPrice?.gross;
  const unitNet = line.unitPrice?.net;
  const resolved = resolveUnitPrice(unitGross, unitNet, isVatIncluded);
  const price = line.price ?? resolved.price;
  const currency = line.currency ?? (resolved.currency || fallbackCurrency);
  const undiscounted = line.undiscountedUnitPrice?.amount ?? price;

  return {
    index,
    item_id: normalizeItemId(sku),
    item_name: (line.name || product?.name || sku).toLowerCase(),
    item_brand: BRAND_NAME.toLowerCase(),
    item_category: line.productType || product?.productType?.slug || 'product',
    item_sub_category: 'regular',
    item_case_material: getCaseMaterialAttributeName(product?.caseMaterial),
    // Checkout fragments expose dial color as `color`; GTM Saleor query uses `dialColor`.
    item_dial_color: getAttributeSlug(product?.color),
    item_strap_color: getStrapColorAttributeName(product?.strapColor),
    item_strap_size: getAttributeSlug(product?.size),
    item_wishlist_compared: 'no',
    price,
    quantity: line.quantity,
    currency,
    coupon,
    discount: Math.max(0, +(undiscounted - price).toFixed(2)),
  };
}

function mergeGtmVariantAttributes(item: Item, variant: GtmProductVariant, coupon?: string): Item {
  const enriched = buildItemFromGtmVariant(variant, item.index, {
    isVatIncluded: true,
    coupon,
  });

  return {
    ...item,
    item_collection: item.item_collection ?? enriched.item_collection,
    item_case_material: item.item_case_material ?? enriched.item_case_material,
    item_stock: enriched.item_stock,
    item_strap_material: item.item_strap_material ?? enriched.item_strap_material,
    item_strap_color: item.item_strap_color ?? enriched.item_strap_color,
    item_gender: item.item_gender ?? enriched.item_gender,
    item_dial_color: item.item_dial_color ?? enriched.item_dial_color,
  };
}

export async function createEcommerceByLines({
  locale,
  lines,
  coupon,
  totalValue,
  includeCurrency = true,
}: {
  locale: string;
  lines: GtmLineInput[];
  coupon?: string;
  totalValue?: number;
  includeCurrency?: boolean;
}): Promise<EcommercePayload> {
  const { channel } = convertLocaleToApiParams(locale);
  const countryCode = getCountryCodeFromChannel(channel);
  const isVatIncluded = !isNoTaxCountry(countryCode) && isTaxIncluded(countryCode);
  const fallbackCurrency = getCurrencyFromChannel(channel);

  if (lines.length === 0) {
    const payload: EcommercePayload = {
      coupon,
      value: +(totalValue ?? 0).toFixed(2),
      items: [],
    };
    if (includeCurrency) {
      payload.currency = fallbackCurrency;
    }
    return payload;
  }

  const skus = lines
    .map((line) => line.sku || line.variant?.sku)
    .filter((sku): sku is string => !!sku);
  const variants = skus.length > 0 ? ((await fetchProductsForGtmBySkus(skus, locale)) ?? []) : [];
  const variantBySku = new Map(
    variants
      .filter((variant) => variant.sku)
      .map((variant) => [variant.sku!.toUpperCase(), variant])
  );

  let value = 0;
  const items: Item[] = lines.map((line, index) => {
    const item = buildItemFromLine(line, index, isVatIncluded, fallbackCurrency, coupon);
    const sku = (line.sku || line.variant?.sku || '').toUpperCase();
    const variant = sku ? variantBySku.get(sku) : undefined;
    const resolved = variant ? mergeGtmVariantAttributes(item, variant, coupon) : item;
    value += resolved.price * resolved.quantity;
    return resolved;
  });

  const payload: EcommercePayload = {
    coupon,
    value: +(totalValue ?? value).toFixed(2),
    items,
  };

  if (includeCurrency && items.length > 0) {
    payload.currency = items[0].currency;
  }

  return payload;
}

export async function createEcommerceBySkus({
  locale,
  skus,
  itemListName,
  coupon,
}: {
  locale: string;
  skus: string[];
  itemListName?: string;
  coupon?: string;
}): Promise<EcommercePayload> {
  const { channel } = convertLocaleToApiParams(locale);
  const gtmCountryCode = getCountryCodeFromChannel(channel);
  const isVatIncluded = !isNoTaxCountry(gtmCountryCode) && isTaxIncluded(gtmCountryCode);
  const fallbackCurrency = getCurrencyFromChannel(channel);

  const filteredSkus = skus.filter((sku) => !!sku && typeof sku === 'string');
  if (filteredSkus.length === 0) {
    return { items: [] };
  }

  const variants = await fetchProductsForGtmBySkus(filteredSkus, locale);
  const variantBySku = new Map(
    variants
      .filter((variant) => variant.sku)
      .map((variant) => [variant.sku!.toUpperCase(), variant])
  );

  const items = filteredSkus.map((sku, index) => {
    const variant = variantBySku.get(sku.toUpperCase());
    if (!variant) {
      return buildFallbackItem(sku, index, fallbackCurrency, itemListName, coupon);
    }

    return buildItemFromGtmVariant(variant, index, { isVatIncluded, itemListName, coupon });
  });

  const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0);

  return {
    coupon,
    value: +value.toFixed(2),
    currency: items[0]?.currency ?? fallbackCurrency,
    items,
  };
}
